// ============================================================================
// Hooks TanStack Query — AFFICHAGES (E1d) : GET /api/postings + fiche + ecritures
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). La logique d'autorisation vit au
// SERVEUR (viewer renvoye par l'API — jamais recalcule au callsite, lecon GA-05) ;
// le SPA consomme viewer.* pour les affordances UI. Erreurs mutation -> code court
// ?error= (deep-linkable) mappe sur un message i18n par les pages (lecon GA-21).
// Piece jointe : METADONNEES seulement (nom/taille/type) — jamais d'URL (blob
// Firebase non migre, GEST-STORAGE-MIGRATION). Aucun upload cote client.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';

export type PostingStatus = 'open' | 'filled' | 'cancelled';
export type GestPostingRole = 'admin' | 'manager' | 'director' | 'employee';

// ─── Liste (F-300..F-303) ────────────────────────────────────────────────────

export interface PostingSummary {
  id: string;
  teamId: string;
  teamName: string | null;
  title: string;
  status: PostingStatus;
  postedAt: string;
  deadline: string | null;
  hasAttachment: boolean;
}

export interface TeamMini {
  id: string;
  name: string;
}

export interface PostingsListResponse {
  postings: PostingSummary[];
  /** Equipes ecrivables par le viewer (F-303) — vide pour manager/employee. */
  writableTeams: TeamMini[];
  viewer: { role: GestPostingRole; email: string };
}

// ─── Fiche (F-306) ────────────────────────────────────────────────────────────

/** Metadonnees d'une piece jointe — JAMAIS d'URL (blob Firebase non migre). */
export interface PostingAttachmentMeta {
  fileName: string;
  contentType: string | null;
  sizeBytes: number | null;
}

export interface PostingDetail {
  id: string;
  teamId: string;
  teamName: string | null;
  teamDirectorEmail: string | null;
  title: string;
  description: string | null;
  tasks: string[];
  status: PostingStatus;
  postedAt: string;
  deadline: string | null;
  attachment: PostingAttachmentMeta | null;
}

export interface PostingDetailResponse {
  posting: PostingDetail;
  viewer: { role: GestPostingRole; email: string; canWrite: boolean };
}

export interface PostingWritePayload {
  title: string;
  description: string | null;
  tasks: string[];
  deadline: string | null;
}

export interface PostingCreatePayload extends PostingWritePayload {
  teamId: string;
}

const LIST_KEY = ['gest', 'postings'] as const;
const detailKey = (id: string) => ['gest', 'postings', id] as const;

/** Code court d'erreur pour `?error=` (parite deep-link) — jamais de code brut (GA-21). */
export function postingErrorCode(err: unknown): string {
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

export function usePostings(showAll: boolean, enabled: boolean) {
  return useQuery({
    queryKey: [...LIST_KEY, showAll ? 'all' : 'open'] as const,
    queryFn: () => api.get<PostingsListResponse>(`/api/postings${showAll ? '?status=all' : ''}`),
    enabled,
  });
}

export function usePostingDetail(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.get<PostingDetailResponse>(`/api/postings/${id}`),
    enabled: enabled && !!id,
    // 404 (id inconnu) et 403 = etats stables — pas de retry.
    retry: false,
  });
}

function useInvalidatePostings() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: LIST_KEY });
    if (id) void qc.invalidateQueries({ queryKey: detailKey(id) });
  };
}

/** F-303 : creation d'un affichage (admin OU director de l'equipe cible, M-301). */
export function useCreatePosting() {
  const invalidate = useInvalidatePostings();
  return useMutation({
    mutationFn: (payload: PostingCreatePayload) =>
      api.post<{ id: string }>('/api/postings', payload),
    onSuccess: () => invalidate(),
  });
}

/** F-306 : edition title/description/tasks/deadline (M-300/M-301). */
export function useUpdatePosting(id: string) {
  const invalidate = useInvalidatePostings();
  return useMutation({
    mutationFn: (payload: PostingWritePayload) =>
      api.put<{ ok: boolean }>(`/api/postings/${id}`, payload),
    onSuccess: () => invalidate(id),
  });
}

/** F-305 : changement de statut open/filled/cancelled (M-300/M-301). */
export function useUpdatePostingStatus(id: string) {
  const invalidate = useInvalidatePostings();
  return useMutation({
    mutationFn: (status: PostingStatus) =>
      api.patch<{ ok: boolean }>(`/api/postings/${id}/status`, { status }),
    onSuccess: () => invalidate(id),
  });
}

/** F-306 : retire la piece jointe (reference NULL, blob Firebase non touche). */
export function useRemovePostingAttachment(id: string) {
  const invalidate = useInvalidatePostings();
  return useMutation({
    mutationFn: () => api.delete<{ ok: boolean }>(`/api/postings/${id}/attachment`),
    onSuccess: () => invalidate(id),
  });
}
